import React, { Component, Fragment } from "react";
import { nonce } from "Utils";
import "assets/article-editor/css/article-editor.css";
import ArticleEditor from "assets/article-editor/article-editor.js";
import { connect } from "formik";
import { modal } from "components/HeadlessModal";
import EditorErrorBoundary from "ErrorHandlers/EditorErrorBoundary";
import ContentPicker from "components/DesignSystem/ContentPicker";
import PrimaryButton from "components/Buttons/PrimaryButton";
import SecondaryButton from "components/Buttons/SecondaryButton";
import "assets/article-editor/plugins/imageposition/imageposition.js";
import "assets/article-editor/plugins/imageresize/imageresize.js";
import "assets/article-editor/plugins/blockcode/blockcode.js";
import "assets/article-editor/plugins/inlineformat/inlineformat.js";
import "assets/article-editor/plugins/specialchars/specialchars.js";
import "assets/article-editor/plugins/underline/underline.js";
import "assets/article-editor/plugins/counter/counter.js";
import "assets/article-editor/plugins/reorder/reorder.js";
import "assets/article-editor/plugins/clips/clips.js";
import "assets/article-editor/snw-plugins/cqflag.js";
import "assets/article-editor/snw-plugins/en.js";

const uuidFromImageString = function (str) {
    const regxp =
        /\/([0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12})\.(sized|original)/;
    const matches = str.match(regxp);
    if (matches) {
        return matches[1];
    }
    return null;
};

const editorInstances = {};

export const cleanupEditors = function () {
    editorInstances.forEach((k, v) => {
        v.destroy();
    });
};

class WrappedEditor extends Component {
    constructor(props) {
        super(props);

        this.autoSaveInterval = 1000;
        this.autoSaveTimeout = null;
        this.externalCss = [
            "/assets/arx-styles.css",
            "/assets/arx-bootstrap.css",
        ];

        // this.articleEditorApp = null;
        this.editorId = nonce();
        this.ref = React.createRef();
        this.copyRef = React.createRef();

        this.handleMediaSelect = this.handleMediaSelect.bind(this);
        this.handleShowRefModal = this.handleShowRefModal.bind(this);
        this.handleCreateCopyRef = this.handleCreateCopyRef.bind(this);

        this.state = {
            editorName: this.props.editorName,
            pickerOpen: false,
            id: this.editorId,
            nonce: this.props.nonce,
            uuid: this.props.formik.values.uuid
                ? this.props.formik.values.uuid
                : null,
        };

        if (this.props.externalCss && this.props.externalCss.length >= 5) {
            const externalCss = this.props.externalCss
                .split(",")
                .map((css) => {
                    let v = css.trim();
                    if (v.indexOf("https") !== 0) {
                        return false;
                    } else {
                        return v;
                    }
                })
                .filter((v) => v);

            this.externalCss = this.externalCss.concat(externalCss);
        }

        console.log("EXTERNAL CSS REQUESTED", this.props.externalCss);
        console.log("EXTERNAL CSS", this.externalCss);

        this.autoSaveHandler = function (t) {
            if (this.autoSaveTimeout) {
                clearTimeout(this.autoSaveTimeout);
            }
            this.autoSaveTimeout = setTimeout(() => {
                if (this.props.autoSaveCallback) {
                    this.props.autoSaveCallback(t);
                }
            }, this.autoSaveInterval);
        };
    }

    handleCreateCopyRef() {
        console.log("VALUE ", this.copyRef.current.value);
        modal.dismiss();
    }

    handleShowRefModal() {
        modal({
            title: "New Copy Reference",
            children: (
                <Fragment>
                    <textarea
                        placeholder="Copy reference"
                        ref={this.copyRef}
                    ></textarea>
                </Fragment>
            ),
            buttons: () => (
                <Fragment>
                    <PrimaryButton onClick={this.handleCreateCopyRef}>
                        Save
                    </PrimaryButton>
                    <SecondaryButton onClick={modal.dismiss}>
                        Cancel
                    </SecondaryButton>
                </Fragment>
            ),
        });
    }

    handleMediaSelect(media) {
        console.log("selected", media);
        if (!media) {
            return;
        }
        editorInstances[this.editorId].image.insert({
            [media.uuid]: {
                url: media.attachment.imgix_url,
                id: "embedded-media-" + media.uuid,
            },
        });
    }

    componentDidMount() {
        console.info("Initializing article editor");
        const self = this;
        try {
            ArticleEditor.add("plugin", "saveHandler", {
                start: function () {
                    this.app.shortcut.add("meta+return", {
                        command: "saveHandler.pushSave",
                        name: "meta+return",
                        title: "Save",
                    });
                },
                pushSave: function (params, e) {
                    if (self.props.onRequestSave) {
                        self.props.onRequestSave();
                    }
                },
            });
            ArticleEditor.add("plugin", "embeddedMedia", {
                translations: {
                    en: {
                        embeddedMedia: {
                            add: "Add",
                            save: "Save",
                        },
                    },
                },
                defaults: {
                    classname: "embedded-media",
                },
                start: function () {
                    this.app.addbar.add("embeddedMedia", {
                        title: "Embed Media",
                        icon: '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>',
                        command: "embeddedMedia.initPicker",
                    });

                    this.app.control.add("loadEmbeddedMedia", {
                        title: "View Media",
                        icon: '<svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 20 20" fill="currentColor"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" /><path fillRule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" /></svg>',
                        command: "embeddedMedia.loadEmbeddedMedia",
                        observer: "embeddedMedia.observeSelection",
                    });
                },
                initPicker: function () {
                    this.app.popup.close();
                    if (self.state.pickerOpen) {
                        // clear picker state of it's already "open"
                        self.setState({ pickerOpen: false });
                        setTimeout(() => {
                            self.setState({ pickerOpen: true });
                        }, 100);
                        return;
                    }
                    self.setState({ pickerOpen: true });
                },
                loadEmbeddedMedia: function (params, btn) {
                    const block = this.app.block.get().getHtml();
                    const attachmentId = uuidFromImageString(block);
                    if (!attachmentId) {
                        return;
                    }
                    if (self.props.onRequestNavigate) {
                        self.props.onRequestNavigate(
                            `/app/attachment/${attachmentId}`
                        );
                    }
                },
                observeSelection: function (obj, name) {
                    if (this.app.block.isType("image")) {
                        return obj;
                    }
                    return false;
                },
            });
            ArticleEditor.add("plugin", "infoflag", {
                translations: {
                    en: {
                        infoflag: {
                            "info-flag": "Add info/comment",
                        },
                    },
                },
                defaults: {
                    icon: '<svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clip-rule="evenodd" /></svg>',
                    editorName: "arx-editor",
                },
                subscribe: {
                    "editor.click": function (event) {
                        if (
                            event.params.e.target &&
                            event.params.e.target.classList.contains(
                                "arx-snw-infoflag"
                            )
                        ) {
                            if (self.props.onRequestLoadCopyRef) {
                                self.props.onRequestLoadCopyRef(
                                    event.params.e.target.getAttribute(
                                        "data-uuid"
                                    ),
                                    event.params.e.target
                                );
                            }
                        }
                    },
                },
                start: function () {
                    this.app.toolbar.add("infoflag", {
                        title: "## infoflag.info-flag ##",
                        icon: this.opts.infoflag.icon,
                        position: { after: "specialchars" },
                        blocks: {
                            all: "editable",
                            except: ["code"],
                        },
                        command: "infoflag.insert",
                    });
                },
                insert: function (_params, button) {
                    if (self.state.uuid) {
                        this.app.popup.create("infoRefPopup", {
                            width: "400px",
                            form: {
                                ref: {
                                    label: "Message/Copy Reference",
                                    type: "textarea",
                                },
                            },
                            footer: {
                                save: {
                                    title: "Save",
                                    command: "infoflag._do_insert",
                                    type: "primary",
                                },
                                cancel: {
                                    title: "Cancel",
                                    command: "popup.close",
                                },
                            },
                        });
                        this.app.popup.open({
                            button: button,
                        });
                    } else {
                        modal.warning({
                            title: "Unsaved content",
                            children: (
                                <div>
                                    Please save before adding a new copy
                                    reference.
                                </div>
                            ),
                        });
                    }
                },
                _do_insert: function (popup) {
                    const data = popup.getData();
                    if (self.props.onCreateNewCopyRef) {
                        self.props.onCreateNewCopyRef(data.ref).then((uuid) => {
                            let $nodes = this.app.insertion.insertHtml(
                                '<span class="arx-snw-infoflag" data-uuid="' +
                                    uuid +
                                    '">' +
                                    this.app.selection.getText() +
                                    "</span>"
                            );
                            this.app.popup.close();
                            return $nodes;

                            // return this.app.inline.set({
                            //     tag: "span",
                            //     attr: {
                            //         class: "arx-snw-infoflag",
                            //         "data-uuid": uuid,
                            //     },
                            // });
                        });
                    }
                },
            });

            // editor instances are loaded into a map in the global scope
            // this keeps them from throwing an error if they're not fully
            // destroyed before they're unmounted.
            editorInstances[this.editorId] = new ArticleEditor(
                this.ref.current,
                {
                    css: "/libs/article-editor-2-4-10/css/",
                    format: [
                        "p",
                        "h1",
                        "h2",
                        "h3",
                        "h4",
                        "h5",
                        "h6",
                        "ul",
                        "ol",
                        "div",
                    ],
                    editor: {
                        focus: false,
                        grammarly: true,
                        lang: "en-SN",
                    },
                    link: {
                        target: "_blank",
                    },
                    addbarAdd: ["card"],
                    addbarHide: ["code"],
                    clips: {
                        items: this.props.clips
                            ? this.props.clips.map((clip) => {
                                  return {
                                      title: clip.label,
                                      html: clip.value,
                                  };
                              })
                            : [],
                    },
                    subscribe: {
                        "editor.change": (event) => {
                            this.props.formik.setFieldValue(
                                "content",
                                event.params.html
                            );
                        },
                        "editor.before.change": (event) => {
                            if (event.isStopped()) {
                                return;
                            }
                            this.autoSaveHandler(event.params.html);
                        },
                        "editor.before.paste": (event) => {
                            if (this.props.cleanPaste) {
                                const html = event.get("html");

                                const newHtml = html
                                    .replace(/data-contrast="(.*?)"/g, "")
                                    .replace(
                                        /data-ccp-([a-z09]*)="(.*?)"/g,
                                        ""
                                    );
                                console.log(html);
                                event.set("html", newHtml);
                            }
                        },
                    },
                    plugins: [
                        "embeddedMedia",
                        "saveHandler",
                        "imageposition",
                        "imageresize",
                        "blockcode",
                        "inlineformat",
                        "specialchars",
                        "underline",
                        "counter",
                        "reorder",
                        "cqflag",
                        "infoflag",
                        "clips",
                    ],
                    custom: {
                        css: this.externalCss,
                    },
                    cqflag: {
                        editorName: this.state.editorName,
                    },
                    autosave: {
                        url: null,
                    },
                    grid: {
                        classname: "row",
                        columns: 12,
                        gutter: "1px",
                        offset: {
                            left: "15px",
                            right: "15px",
                        },
                        patterns: {
                            "6|6": "col-12 col-md-6|col-12 col-md-6",
                            "4|4|4":
                                "col-12 col-md-4|col-12 col-md-4|col-12 col-md-4",
                            "3|3|3|3":
                                "col-12 col-md-3|col-12 col-md-3|col-12 col-md-3|col-12 col-md-3",
                            "2|2|2|2|2|2":
                                "col-12 col-md-2|col-12 col-md-2|col-12 col-md-2|col-12 col-md-2|col-12 col-md-2|col-12 col-md-2",
                            "3|6|3":
                                "col-12 col-md-3|col-12 col-md-6|col-12 col-md-3",
                            "2|8|2":
                                "col-12 col-md-2|col-12 col-md-8|col-12 col-md-2",
                            "5|7": "col-12 col-md-5|col-12 col-md-7",
                            "7|5": "col-12 col-md-7|col-12 col-md-5",
                            "4|8": "col-12 col-md-4|col-12 col-md-8",
                            "8|4": "col-12 col-md-8|col-12 col-md-4",
                            "3|9": "col-12 col-md-3|col-12 col-md-9",
                            "9|3": "col-12 col-md-9|col-12 col-md-3",
                            "2|10": "col-12 col-md-2|col-12 col-md-10",
                            "10|2": "col-12 col-md-10|col-12 col-md-2",
                            12: "col-12",
                        },
                    },
                    code: {
                        template: '<figure><div class="embed"></div></figure>',
                    },
                    templates: {
                        ss1: {
                            name: "Simple Section",
                            html: '<figure class="outset-both"> <img src="https://snworksceo.imgix.net/~text?fm=png&amp;txtsize=38&amp;w=2000&amp;h=400&amp;bg=cccccc&amp;txt=Placeholder!&amp;txtclr=333333&amp;ta=center,middle"> </figure><h3> Section Hed </h3><p> Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. </p><figure class="float-left" style="max-width: 348px;"> <img src="https://snworksceo.imgix.net/~text?fm=png&amp;txtsize=18&amp;w=500&amp;h=400&amp;bg=cccccc&amp;txt=Placeholder!&amp;txtclr=333333&amp;ta=center,middle" width="348" height="279" style="width: 348px; max-width: 348px; height: 279px;"> </figure><p> Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. </p><p> Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. </p><h3> Section Hed</h3> <p> Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. </p>',
                        },
                        ss2: {
                            name: "Embedded Dynamic Template",
                            html: '<p> Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. </p><figure><div data-ceo-include="page/embedded-template-name.tpl"> Select the "code" icon to the left and update the include path to dynamically inject a CEO template. </div></figure><p> Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua. At vero eos et accusam et justo duo dolores et ea rebum. Stet clita kasd gubergren, no sea takimata sanctus est Lorem ipsum dolor sit amet. </p>',
                        },
                        // ex2: {
                        //     name: "Example 2",
                        //     image: "",
                        //     html: "",
                        // },
                    },
                }
            );
        } catch (error) {
            console.error("Error initializing article editor", error);
        }
    }

    componentWillUnmount() {
        if (editorInstances[this.editorId]) {
            // Force the editor to unfocus, otherwise it'll throw an error
            // when it's destroyed and remounted.
            console.info("Unmounting article editor");
            try {
                editorInstances[this.editorId].app.editor.setBlur();
                editorInstances[this.editorId].destroy();
                console.info("Unmounted article editor");
            } catch (error) {
                console.error("Error unmounting article editor", error);
            }
        }
    }

    getEditorContent() {
        return editorInstances[this.editorId].editor.getContent();
    }

    static getDerivedStateFromProps(props, state) {
        if (editorInstances[state.id] && props.disabled) {
            editorInstances[state.id].app.disable();
        } else if (editorInstances[state.id]) {
            editorInstances[state.id].app.enable();
        }
        if (state.uuid !== props.formik.values.uuid) {
            // force editor to replace content if the nonce changes
            editorInstances[state.id].app.editor.setContent({
                html: props.formik.values.content
                    ? props.formik.values.content
                    : "",
            });
            return {
                uuid: props.formik.values.uuid,
            };
        }
        return null;
        // if (state.nonce !== props.nonce) {
        //     // force editor to replace content if the nonce changes
        //     editorInstances[state.id].app.editor.setContent({
        //         html: props.formik.values.content
        //             ? props.formik.values.content
        //             : "",
        //     });
        //     return {
        //         nonce: props.nonce,
        //     };
        // }
        // return null;
    }

    render() {
        return (
            <EditorErrorBoundary>
                <textarea
                    key={this.editorId}
                    {...this.props.formik.getFieldProps()}
                    value={
                        this.props.formik.values.content
                            ? this.props.formik.values.content
                            : ""
                    }
                    ref={this.ref}
                ></textarea>

                <ContentPicker
                    open={this.state.pickerOpen}
                    onRequestClose={() => {
                        this.setState({ pickerOpen: false });
                    }}
                    onSelect={this.handleMediaSelect}
                    allowMultiple={false}
                    mediaType="image"
                    type="media"
                    listType="grid"
                />
            </EditorErrorBoundary>
        );
    }
}

export default connect(WrappedEditor);
